import React, { useState } from 'react'

import { getErrorMessage } from 'utils'

import { useSnackbar } from 'contexts'
import { useCompanyMutationsQuery, useStagePendingSignupGrant } from 'hooks/api'

import { Stack, Typography } from '@mui/material'

import { Modal, Button, Input, TextArea } from '@repo/ui'
import type { Company } from 'types'

interface GrantActivationsModalProps {
  /** An existing company — grants credits directly. */
  company?: Company | null
  /**
   * VRG-247 (Waitlist pre-load, Batch 4b): a Waitlist entry that has no
   * company yet — stages the grant instead, applied automatically once a
   * company is created for this signup. Mutually exclusive with `company`.
   */
  signupId?: string
  /**
   * VRG-545: an ALLOWLIST entry — allowed to sign up, no company and possibly
   * no waitlist row either. Stages the grant against the signups row the API
   * resolves (or creates) for this address. Mutually exclusive with `company`
   * and `signupId`.
   */
  email?: string
  open: boolean
  onClose: () => void
  refetch: () => void
}

export const GrantActivationsModal = (props: GrantActivationsModalProps) => {
  const { company, signupId, email, open, onClose = () => {}, refetch } = props

  // Staging (no company yet) vs granting to a live company. Both pre-company
  // entry points read the same to the user — the copy and the confirm verb
  // follow this, not the particular key used to resolve the row.
  const isStaging = !company?.id && (!!signupId || !!email)

  const { open: notification } = useSnackbar()

  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')

  const { grantActivations, isGrantingActivations } = useCompanyMutationsQuery({
    onSuccess: () => {
      notification({
        type: 'success',
        title: 'Activation credits granted.',
      })
      handleClose()
      refetch()
    },
    onError: error => {
      const message = getErrorMessage(error)
      notification({
        type: 'error',
        title: message,
      })
    },
  })

  const { mutate: stagePendingGrant, isPending: isStagingGrant } = useStagePendingSignupGrant({
    onSuccess: () => {
      notification({
        type: 'success',
        title: 'Activation grant staged. It will apply once this signup becomes a company.',
      })
      handleClose()
      refetch()
    },
    onError: error => {
      notification({
        type: 'error',
        title: error?.message || 'Failed to stage activation grant',
      })
    },
  })

  const isSubmitting = isGrantingActivations || isStagingGrant

  const parsedAmount = Number(amount)
  const isValidAmount =
    amount !== '' && Number.isInteger(parsedAmount) && parsedAmount > 0

  const handleConfirm = () => {
    if (!isValidAmount || !reason) return
    if (company?.id) {
      grantActivations({
        companyId: company.id,
        amount: parsedAmount,
        reason,
      })
    } else if (signupId) {
      stagePendingGrant({
        signupId,
        activationAmount: parsedAmount,
        activationReason: reason,
      })
    } else if (email) {
      stagePendingGrant({
        email,
        activationAmount: parsedAmount,
        activationReason: reason,
      })
    }
  }

  const handleClose = () => {
    setAmount('')
    setReason('')
    onClose()
  }

  return (
    <Modal
      title="Grant activations"
      open={open}
      onClose={handleClose}
      actions={
        <>
          <Button
            disableElevation
            variant="secondary"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>

          <Button
            disableElevation
            onClick={handleConfirm}
            disabled={isSubmitting || !isValidAmount || !reason}
          >
            {isSubmitting ? 'Saving...' : isStaging ? 'Stage grant' : 'Grant activations'}
          </Button>
        </>
      }
    >
      <Stack spacing="16px">
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {isStaging
            ? "This company doesn't exist yet. The grant is staged now and applies automatically as soon as this signup becomes a company. Granted activations never expire."
            : "Granted activations never expire. They're used after the plan's included allowance, independent of any subscription comp."}
        </Typography>

        <Input
          label="Amount"
          type="number"
          placeholder="e.g. 100"
          value={amount}
          onValueChange={setAmount}
        />

        <TextArea
          label="Reason"
          placeholder="Why are these activations being granted?"
          value={reason}
          onValueChange={setReason}
          minRows={2}
        />
      </Stack>
    </Modal>
  )
}
